import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router';
import {
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material';
import { GridColDef, GridRowSelectionModel, useGridApiRef } from '@mui/x-data-grid-pro';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import { enqueueSnackbar } from 'notistack';
import { StyleDataGridPro } from '../../components/data-grid/style';
import CustomBreadcrumbs from '../../components/custom-breadcrumbs';
import Iconify from '../../components/iconify';
import SelectionBar from '../../components/SelectionBar';
import RoleBasedGuard from '../../auth/RoleBasedGuard';
import ConfirmDialog from '../../components/confirm-dialog/ConfirmDialog';
import { useSettingsContext } from '../../components/settings';
import useResponsive from '../../hooks/useResponsive';
import useGetElementHeight from '../../hooks/useGetElementHeight';
import { useLocales } from '../../locales';
import { PATH_DASHBOARD } from '../../routes/paths';
import {
  selectAllTourBundle,
  useGetTourBundlesQuery,
  useDeleteTourBundlesMutation,
} from '../../redux/slices/tourSlice';
import { setDataGridLocale } from '../../utils/dataGridLocaleHelper';
import { customToolbar } from '../../utils/dataGridToolbarHelper';
import {
  getDataGridStateFromLocalStorage,
  restoreDataGridStateWithCustomAction,
  saveDataGridStateToLocalStorage,
  setCheckboxToPinnedColumns,
} from '../../utils/dataGridStateHelper';

export default function TourBundleList() {
  const { translate } = useLocales();
  const navigate = useNavigate();
  const apiRef = useGridApiRef();
  const { themeStretch } = useSettingsContext();
  const { isLoading, isSuccess, isError, error } = useGetTourBundlesQuery();
  const tourBundles = useSelector(selectAllTourBundle);
  const [checkboxSelection, setCheckboxSelection] = useState<GridRowSelectionModel | []>([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isDesktop = useResponsive('up', 'lg');
  const breadcrumbHeight = useGetElementHeight('custom-breadcrumb-wrapper');

  const [deleteTourBundles] = useDeleteTourBundlesMutation();

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: `${translate('tourBundle.bundleName')}`,
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'description',
      headerName: `${translate('tourBundle.description')}`,
      flex: 2,
      minWidth: 300,
    },
    {
      field: 'bundleColor',
      headerName: `${translate('tourBundle.color')}`,
      width: 100,
      renderCell: (params) => (
        <div
          style={{
            width: 40,
            height: 40,
            backgroundColor: params.value,
            borderRadius: 4,
            border: '1px solid #ccc',
          }}
        />
      ),
    },
  ];

  const handleDeleteAndSendIds = async () => {
    setIsDeleting(true);
    try {
      const tourBundleIdsToDelete: string[] = Object.values(
        checkboxSelection.map((id) => id.toString())
      );
      await deleteTourBundles({ ids: tourBundleIdsToDelete }).unwrap();
      enqueueSnackbar(`${translate('delete_successful')}`);
    } catch (e) {
      console.error(e);
      enqueueSnackbar(`${translate('send_delete_failed')}`, { variant: 'error' });
    }
    setIsDeleting(false);
    setCheckboxSelection([]);
    setOpenDeleteDialog(false);
  };

  const CustomToolbarWrapper = () => customToolbar();

  let tableContent: any;

  if (isLoading) {
    tableContent = <CircularProgress />;
  } else if (isError) {
    tableContent = <>{error?.toString()}</>;
  } else if (isSuccess) {
    tableContent = (
      <StyleDataGridPro
        columns={columns}
        rows={tourBundles}
        apiRef={apiRef}
        slots={{ toolbar: CustomToolbarWrapper }}
        disableRowSelectionOnClick={true}
        onRowDoubleClick={(params, event) => {
          event.defaultMuiPrevented = true;
          console.log(`row double click: ${params.id}`);
          navigate(PATH_DASHBOARD.basicSetting.tourBundle.view(params.id.toString()));
        }}
        checkboxSelection
        keepNonExistentRowsSelected
        rowSelectionModel={checkboxSelection}
        onRowSelectionModelChange={(id) => {
          setCheckboxSelection(id);
        }}
        onColumnOrderChange={() => {
          saveDataGridStateToLocalStorage('tourBundleList', apiRef.current.exportState());
        }}
        onFilterModelChange={() => {
          saveDataGridStateToLocalStorage('tourBundleList', apiRef.current.exportState());
        }}
        onColumnVisibilityModelChange={() => {
          saveDataGridStateToLocalStorage('tourBundleList', apiRef.current.exportState());
        }}
        onColumnResize={() => {
          saveDataGridStateToLocalStorage('tourBundleList', apiRef.current.exportState());
        }}
        onPinnedColumnsChange={() => {
          saveDataGridStateToLocalStorage('tourBundleList', apiRef.current.exportState());
        }}
        onSortModelChange={() => {
          saveDataGridStateToLocalStorage('tourBundleList', apiRef.current.exportState());
        }}
        isDesktop={isDesktop}
        breadcrumbHeight={breadcrumbHeight}
        localeText={setDataGridLocale()}
      />
    );
  }

  useEffect(() => {
    const dataGridState = getDataGridStateFromLocalStorage('tourBundleList');
    if (dataGridState && apiRef && isSuccess) {
      const state = setCheckboxToPinnedColumns(dataGridState);
      restoreDataGridStateWithCustomAction(apiRef, state);
    }
  });

  return (
    <>
      <Helmet>
        <title>{`${translate('tourBundle.plural')}`}</title>
      </Helmet>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <CustomBreadcrumbs
          heading={`${translate('tourBundle.plural')}`}
          links={[
            { name: `${translate('dashboard')}`, href: PATH_DASHBOARD.root },
            {
              name: `${translate('tourBundle.plural')}`,
              href: PATH_DASHBOARD.basicSetting.tourBundle.root,
            },
            { name: `${translate('list')}` },
          ]}
          action={
            <RoleBasedGuard roles={['tour-write']}>
              <Button
                variant="contained"
                component={RouterLink}
                to={PATH_DASHBOARD.basicSetting.tourBundle.new}
                startIcon={<Iconify icon="eva:plus-fill" />}
                sx={{ textTransform: 'none' }}
              >
                {`${translate('tourBundle.new')}`}
              </Button>
            </RoleBasedGuard>
          }
        />
        <Stack>
          <Card sx={{ width: '100%' }}>
            <SelectionBar
              countSelected={Object.keys(checkboxSelection).length}
              actions={
                <Stack
                  spacing={2}
                  direction="row"
                  divider={<Divider orientation="vertical" flexItem color="primary" />}
                >
                  <ul>
                    <Tooltip title={`${translate('delete')}`}>
                      <IconButton
                        disabled={Object.keys(checkboxSelection).length === 0}
                        color="primary"
                        onClick={() => setOpenDeleteDialog(true)}
                      >
                        <Iconify icon="mdi:trash-can-outline" />
                      </IconButton>
                    </Tooltip>
                  </ul>
                </Stack>
              }
            />
            {tableContent}
          </Card>
        </Stack>
        <ConfirmDialog
          open={openDeleteDialog}
          title={`${translate('tourBundle.deleteTourBundles')}`}
          content={
            Object.keys(checkboxSelection).length > 0 && (
              <Stack direction="row" useFlexGap flexWrap="wrap">
                {(checkboxSelection as string[]).map((id, index) => (
                  <Chip
                    key={index}
                    label={`${id} - ${tourBundles?.find((bundle) => bundle.id === id)?.name || ''}`}
                    sx={{ m: 0.5 }}
                    color="secondary"
                    variant="outlined"
                  />
                ))}
              </Stack>
            )
          }
          action={
            <Button
              variant="contained"
              loading={isDeleting}
              color="error"
              onClick={handleDeleteAndSendIds}
            >
              {`${translate('delete')}`}
            </Button>
          }
          onClose={() => setOpenDeleteDialog(false)}
        />
      </Container>
    </>
  );
}
